import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControlLabel,
  Grid,
  MenuItem,
  Paper,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import { useAuth } from '../contexts/AuthContext';
import { ModelSelector } from '../components/ModelSelector';
import { useApis, credits, CycleStatusChip } from '../components/learning/common';
import { FeatureFlagInfo, formatDate, learningPath, SystemSettings } from '../services/learningApi';

type Overview = Awaited<ReturnType<ReturnType<typeof useApis>['admin']['overview']>>;
type AnalyzerFn = { name: string; function_id: string; stage: string; description: string; allowed_models: string[] };
type CustomerOption = { id: string; name: string | null; customer_tag?: string };

const FlagCard: React.FC<{ flag: FeatureFlagInfo; onSaved: (f: FeatureFlagInfo) => void }> = ({ flag, onSaved }) => {
  const apis = useApis();
  const [allOn, setAllOn] = useState(flag.enabled_for_all);
  const [customers, setCustomers] = useState<CustomerOption[]>(flag.customers.map((c) => ({ id: c.id, name: c.name })));
  const [options, setOptions] = useState<CustomerOption[]>([]);
  const [search, setSearch] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (search.trim().length < 2) return;
    const t = setTimeout(() => {
      apis.admin
        .searchCustomers(search.trim())
        .then((r) => setOptions(r.data.map((c) => ({ id: c.id, name: c.name, customer_tag: c.customer_tag }))))
        .catch(() => setOptions([]));
    }, 300);
    return () => clearTimeout(t);
  }, [apis, search]);

  const dirty = allOn !== flag.enabled_for_all || customers.map((c) => c.id).sort().join() !== flag.customers.map((c) => c.id).sort().join();

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      const f = await apis.admin.updateFlag(flag.key, { enabled_for_all: allOn, customer_ids: customers.map((c) => c.id) });
      onSaved(f);
      setSaved(true);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const missing = flag.customers.filter((c) => !c.exists);

  return (
    <Paper variant="outlined" sx={{ p: 2.5, mb: 2 }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
        <Typography variant="subtitle1" fontWeight={600}>
          {flag.key}
        </Typography>
        <Chip size="small" label={flag.enabled_for_all ? 'on for everyone' : `${flag.customers.length} customer(s)`} color={flag.enabled_for_all ? 'success' : 'default'} />
      </Stack>
      {flag.description && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          {flag.description}
        </Typography>
      )}
      {error && (
        <Alert severity="error" sx={{ mb: 1.5 }}>
          {error}
        </Alert>
      )}
      {missing.length > 0 && (
        <Alert severity="warning" sx={{ mb: 1.5 }}>
          Unknown customer ids on this flag: {missing.map((m) => m.id).join(', ')}
        </Alert>
      )}
      <FormControlLabel
        control={<Switch checked={allOn} onChange={(e) => { setAllOn(e.target.checked); setSaved(false); }} />}
        label="Enabled for all customers"
      />
      <Autocomplete
        multiple
        size="small"
        disabled={allOn}
        options={options}
        value={customers}
        filterOptions={(x) => x}
        isOptionEqualToValue={(a, b) => a.id === b.id}
        getOptionLabel={(o) => `${o.name || o.id}${o.customer_tag ? ` (${o.customer_tag})` : ''}`}
        onInputChange={(_, v) => setSearch(v)}
        onChange={(_, v) => { setCustomers(v); setSaved(false); }}
        renderTags={(v, getTagProps) => v.map((c, i) => <Chip size="small" label={c.name || c.id} {...getTagProps({ index: i })} />)}
        renderInput={(p) => <TextField {...p} label="Enabled for these customers" placeholder="Search by name, tag or id" helperText={allOn ? 'Ignored while enabled for all' : undefined} />}
        sx={{ mt: 1 }}
      />
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 2 }}>
        <Button variant="contained" onClick={save} disabled={!dirty || saving}>
          {saving ? 'Saving…' : 'Save'}
        </Button>
        {saved && !dirty && (
          <Typography variant="body2" color="success.main">
            Saved.
          </Typography>
        )}
        <Box sx={{ flex: 1 }} />
        <Typography variant="caption" color="text.secondary">
          {flag.updated_by ? `Last changed by ${flag.updated_by} · ` : ''}
          {formatDate(flag.updated_at)}
        </Typography>
      </Stack>
    </Paper>
  );
};

const MODEL_STAGES: { stage: keyof SystemSettings['models']; label: string; help: string }[] = [
  { stage: 'profile', label: 'Profile model', help: 'Designs what to learn' },
  { stage: 'extract', label: 'Extract model', help: 'Reads runs in batches' },
  { stage: 'review', label: 'Review model', help: 'Writes the report and suggestions' },
  { stage: 'compile', label: 'Combine model', help: 'Merges overlapping suggestions on “Combine & apply”' },
];

const SettingsCard: React.FC<{ initial: SystemSettings; analyzers: AnalyzerFn[] }> = ({ initial, analyzers }) => {
  const apis = useApis();
  const { setIsPublicMode } = useAuth();
  const navigate = useNavigate();
  const [settings, setSettings] = useState<SystemSettings>(initial);
  const [saved, setSaved] = useState<SystemSettings>(initial);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const dirty = JSON.stringify(settings) !== JSON.stringify(saved);

  const set = <K extends keyof SystemSettings>(k: K, v: SystemSettings[K]) => {
    setMessage(null);
    setSettings((s) => ({ ...s, [k]: v }));
  };

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      const res = await apis.admin.saveSettings(settings);
      setSettings(res.settings);
      setSaved(res.settings);
      setMessage('Saved.');
    } catch (e: any) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const num = (label: string, key: 'max_concurrent_cycles' | 'runs_per_extract_call' | 'max_candidates_scanned' | 'default_credit_cap', helperText: string) => (
    <TextField
      fullWidth
      size="small"
      type="number"
      label={label}
      value={settings[key]}
      onChange={(e) => set(key, Number(e.target.value))}
      helperText={helperText}
    />
  );

  return (
    <Paper variant="outlined" sx={{ p: 2.5, mb: 2 }}>
      <Typography variant="subtitle1" fontWeight={600} gutterBottom>
        Analyzer
      </Typography>
      {error && (
        <Alert severity="error" sx={{ mb: 1.5 }}>
          {error}
        </Alert>
      )}
      <FormControlLabel
        control={<Switch color="warning" checked={settings.kill_switch} onChange={(e) => set('kill_switch', e.target.checked)} />}
        label={settings.kill_switch ? 'Paused platform-wide — no cycles start' : 'Running (flip to pause all cycles)'}
      />
      <Grid container spacing={2} sx={{ mt: 0.5 }}>
        {MODEL_STAGES.map(({ stage, label, help }) => (
          <Grid item xs={12} sm={6} md={3} key={stage}>
            <ModelSelector
              size="small"
              fullWidth
              label={label}
              value={settings.models[stage]}
              onChange={(v) => set('models', { ...settings.models, [stage]: v as string })}
            />
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
              {help}
            </Typography>
          </Grid>
        ))}
        <Grid item xs={12}>
          <FormControlLabel
            control={<Switch checked={settings.allow_customer_model_override} onChange={(e) => set('allow_customer_model_override', e.target.checked)} />}
            label="Let customers choose their own analyzer models"
          />
        </Grid>
        <Grid item xs={6} md={3}>
          {num('Concurrent cycles', 'max_concurrent_cycles', 'Across the platform')}
        </Grid>
        <Grid item xs={6} md={3}>
          {num('Runs per analysis call', 'runs_per_extract_call', 'Batching for extraction')}
        </Grid>
        <Grid item xs={6} md={3}>
          {num('Max runs considered', 'max_candidates_scanned', 'Per cycle, before sampling')}
        </Grid>
        <Grid item xs={6} md={3}>
          {num('Default credit cap', 'default_credit_cap', 'Upper bound for automatic caps')}
        </Grid>
      </Grid>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 2 }}>
        <Button variant="contained" onClick={save} disabled={!dirty || saving}>
          {saving ? 'Saving…' : 'Save'}
        </Button>
        {message && (
          <Typography variant="body2" color="success.main">
            {message}
          </Typography>
        )}
      </Stack>

      <Typography variant="subtitle2" sx={{ mt: 3, mb: 1 }}>
        Analyzer prompts
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        These are public AI functions. Editing opens them in public mode; changes apply to the next cycle.
      </Typography>
      <Stack spacing={1}>
        {analyzers.map((a) => (
          <Stack key={a.name} direction="row" spacing={1.5} alignItems="center">
            <Chip size="small" label={a.stage} sx={{ minWidth: 70 }} />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="body2" fontFamily="monospace">
                {a.name}
              </Typography>
              <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
                {a.description}
              </Typography>
            </Box>
            <Button
              size="small"
              startIcon={<EditIcon />}
              onClick={() => {
                setIsPublicMode(true);
                navigate(`/dashboard/ai-functions?id=${a.function_id}`);
              }}
            >
              Edit
            </Button>
          </Stack>
        ))}
      </Stack>
    </Paper>
  );
};

const OverviewCard: React.FC = () => {
  const apis = useApis();
  const [days, setDays] = useState(30);
  const [data, setData] = useState<Overview | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setData(null);
    apis.admin
      .overview(days)
      .then(setData)
      .catch((e) => setError(e.message));
  }, [apis, days]);

  return (
    <Paper variant="outlined" sx={{ p: 2.5 }}>
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
        <Typography variant="subtitle1" fontWeight={600}>
          Usage
        </Typography>
        <TextField select size="small" value={days} onChange={(e) => setDays(Number(e.target.value))}>
          {[7, 30, 90].map((d) => (
            <MenuItem key={d} value={d}>
              Last {d} days
            </MenuItem>
          ))}
        </TextField>
        <Box sx={{ flex: 1 }} />
        {data && (
          <Stack direction="row" spacing={1}>
            <Chip size="small" label={credits(data.total_credits)} />
            {Object.entries(data.cycle_status_counts).map(([s, n]) => (
              <Chip key={s} size="small" variant="outlined" label={`${s}: ${n}`} />
            ))}
          </Stack>
        )}
      </Stack>
      {error && <Alert severity="error">{error}</Alert>}
      {!data && !error && <CircularProgress size={24} />}
      {data && (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Customer</TableCell>
                <TableCell>Target</TableCell>
                <TableCell>Enabled</TableCell>
                <TableCell>Schedule</TableCell>
                <TableCell>Last cycle</TableCell>
                <TableCell align="right">
                  <Tooltip title="Suggestions waiting for the owner to accept or dismiss">
                    <span>Open suggestions</span>
                  </Tooltip>
                </TableCell>
                <TableCell align="right">
                  <Tooltip title="Learnings waiting for review before they are used">
                    <span>To review</span>
                  </Tooltip>
                </TableCell>
                <TableCell align="right">Cycles</TableCell>
                <TableCell align="right">Credits</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.configs.length === 0 && (
                <TableRow>
                  <TableCell colSpan={9}>
                    <Typography color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                      No targets configured yet.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
              {data.configs.map((c) => (
                <TableRow key={c.id}>
                  <TableCell>{c.customer_name || c.customer_id}</TableCell>
                  <TableCell>
                    {c.target_name || c.target_id}
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                      {c.target_type}
                    </Typography>
                  </TableCell>
                  <TableCell>{c.enabled ? <Chip size="small" color="success" label="on" /> : <Chip size="small" label="off" />}</TableCell>
                  <TableCell>{c.schedule}</TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <CycleStatusChip status={c.last_cycle_status} />
                      <Typography variant="caption">{formatDate(c.last_cycle_at)}</Typography>
                    </Stack>
                  </TableCell>
                  <TableCell align="right">
                    {c.open_suggestions ? <Chip size="small" color="warning" variant="outlined" label={c.open_suggestions} /> : '–'}
                  </TableCell>
                  <TableCell align="right">
                    {c.waiting_entries ? <Chip size="small" color="info" variant="outlined" label={c.waiting_entries} /> : '–'}
                  </TableCell>
                  <TableCell align="right">{c.cycles}</TableCell>
                  <TableCell align="right">{c.credits.toFixed(2)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Paper>
  );
};

export const AdminSelfImprovement: React.FC = () => {
  const apis = useApis();
  const [flags, setFlags] = useState<FeatureFlagInfo[] | null>(null);
  const [settings, setSettings] = useState<{ settings: SystemSettings; analyzer_functions: AnalyzerFn[] } | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    apis.admin
      .flags()
      .then((r) => setFlags(r.flags))
      .catch((e) => setError(e.message));
    apis.admin
      .settings()
      .then(setSettings)
      .catch((e) => setError(e.message));
  }, [apis]);

  useEffect(load, [load]);

  const learningFlags = useMemo(() => flags || [], [flags]);

  return (
    <Box sx={{ maxWidth: 1200 }}>
      <Typography variant="h5" gutterBottom>
        Self-improvement
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Who can use it, which models analyze runs, and what it costs. Customers manage each target from its Learning page (e.g.{' '}
        <code>{learningPath('chain', '<chain id>')}</code>).
      </Typography>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {!flags && !error && <CircularProgress size={24} />}
      {learningFlags.map((f) => (
        <FlagCard key={f.key} flag={f} onSaved={(nf) => setFlags((all) => (all || []).map((x) => (x.key === nf.key ? nf : x)))} />
      ))}
      {settings && <SettingsCard initial={settings.settings} analyzers={settings.analyzer_functions} />}
      <OverviewCard />
    </Box>
  );
};

export default AdminSelfImprovement;
